<template>
  <div>
    <transition name="u-fade-in">
      <ul v-if="show.meta" class="RadarChart__meta">
        <li class="RadarChart__metaItem">
          Accuracy: <strong id="score">0</strong>%
        </li>
        <li class="RadarChart__metaItem">
          Level: <strong>{{ scoreLabels }}</strong>
        </li>
      </ul>
    </transition>
    <div
      v-show="show.chart"
      class="RadarChart"
      id="chart"
      :class="classes"
    ></div>
  </div>
</template>

<script>
const d3 = require('d3')

import { mapActions } from 'vuex'
export default {
  name: 'RadarChart',

  props: {
    theme: {
      type: String,
      default: 'orchard'
    },
    activeLabels: {
      type: Array,
      required: true
    },
    scores: {
      type: Array,
      required: true
    },
    flavours: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      show: {
        chart: false,
        meta: false
      }
    }
  },

  mounted() {
    this.initChart()
    setTimeout(() => {
      this.show.meta = true
    }, 0)
    setTimeout(() => {
      this.animateValue('score', 0, this.accuracyScore, 1000)
      this.show.chart = true
      this.$emit('animationFinished')
      this.updateRating(this.scoreLabels)
    }, 500)
  },

  computed: {
    accuracyScore() {
      let activeFlavours = this.flavours.filter(item => item.active)
      let sum = 0
      this.scores.forEach((score, index) => {
        if (activeFlavours[index].fractions) {
          sum +=
            activeFlavours[index].fractions[score - 1] *
            activeFlavours[index].weight
        }
      })
      const totalPercent = sum / activeFlavours.length
      return Math.round(totalPercent)
      //this.$log.info('activeFlavours', activeFlavours)
    },
    scoreLabels() {
      switch (true) {
        case this.accuracyScore < 20:
          return 'Explorer'
        case this.accuracyScore < 40:
          return 'Malt Lover'
        case this.accuracyScore < 70:
          return 'Connoisseur'
        default:
          return 'Master'
      }
    },
    classes() {
      return {
        [`RadarChart--theme--${this.theme}`]: this.theme
      }
    }
  },
  methods: {
    ...mapActions(['updateRating']),
    initChart() {
      const screenWidth = 310

      const margin = { left: 20, top: 20, right: 20, bottom: 20 },
        width = Math.min(screenWidth, 500) - margin.left - margin.right,
        height = Math.min(screenWidth, 500) - margin.top - margin.bottom

      const svg = d3
        .select('#chart')
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('class', 'wrapper')
        .attr(
          'transform',
          'translate(' +
            (width / 2 + margin.left) +
            ',' +
            (height / 2 + margin.top) +
            ')'
        )

      //////////////////////////////////////////////////////////////
      ///////////////////// Gradients //////////////////////////////
      //////////////////////////////////////////////////////////////

      const svgDefs = svg.append('defs')

      const mainGradient = svgDefs
        .append('linearGradient')
        .attr('id', 'mainGradient')
        .attr('gradientTransform', 'rotate(90)')

      // Create the stops of the main gradient. Each stop will be assigned
      // a class to style the stop using CSS.
      mainGradient
        .append('stop')
        .attr('class', 'stop-left')
        .attr('offset', '0')

      mainGradient
        .append('stop')
        .attr('class', 'stop-center')
        .attr('offset', '50%')

      mainGradient
        .append('stop')
        .attr('class', 'stop-right')
        .attr('offset', '100%')

      //////////////////////////////////////////////////////////////
      ///////////////////// GRID LINES /////////////////////////////
      //////////////////////////////////////////////////////////////

      let radialScale = d3
        .scaleLinear()
        .domain([0, 10])
        .range([0, 100])
      let ticks = [2, 4, 6, 8, 10]

      ticks.forEach(t =>
        svg
          .append('circle')
          .attr('cx', 0)
          .attr('cy', 0)
          .attr('fill', 'none')
          .attr('stroke', '#D8D8D8')
          .attr('opacity', 0.1)
          .attr('r', radialScale(t))
      )
      /*
      ticks.forEach(t =>
        svg.append("text")
          .attr("x", 0)
          .attr("fill", "white")
          .attr("y", 0 - radialScale(t))
          .text('sasasa')
      ); */

      // White dot in the middle
      /*
      svg.append("circle")
        .attr("cx", 0)
        .attr("cy", 0)
        .attr("fill", '#FFF')
        .attr("stroke", '#FFF')
        .attr("r", 3)
        */

      //////////////////////////////////////////////////////////////
      ///////////////////// Data &  Scales /////////////////////////
      //////////////////////////////////////////////////////////////

      //Some random data
      let labelData = [
        { name: 'Pear', value: 12.5, active: false, score: 4, order: 2 },
        { name: 'Pineapple', value: 12.5, active: false, score: 5, order: 1 },
        { name: 'Floral', value: 12.5, active: false, score: 0, order: 8 },
        { name: 'Vanilla', value: 12.5, active: false, score: 3, order: 7 },
        { name: 'Toffee', value: 12.5, active: false, score: 0, order: 6 },
        { name: 'Spice', value: 12.5, active: false, score: 0, order: 5 },
        { name: 'Peaty', value: 12.5, active: false, score: 5, order: 4 },
        { name: 'Smoky', value: 12.5, active: false, score: 0, order: 3 }
      ]

      // Combine label names with scores
      let labelAndScores = {}
      this.activeLabels.forEach((v, i) => {
        let obj = {
          [v]: Number(this.scores[i])
        }
        labelAndScores = {
          ...labelAndScores,
          ...obj
        }
      })

      //console.log('labelAndScores', labelAndScores)

      // Get active state from passed URL props
      this.activeLabels.forEach(label => {
        const index = labelData.findIndex(item => {
          return item.name === label
        })
        labelData[index].active = true
      })

      //Create an arc function
      const arc = d3
        .arc()
        .innerRadius((width * 0.83) / 2)
        .outerRadius((width * 0.75) / 2 + 21)

      //Turn the pie chart 90 degrees counter clockwise, so it starts at the left
      const pie = d3
        .pie()
        .startAngle((-90 * Math.PI) / 180)
        .endAngle((-90 * Math.PI) / 180 + 2 * Math.PI)
        .value(function(d) {
          return d.value
        })
        .padAngle(0.05) // spacing between slices
        .sort(null)

      //////////////////////////////////////////////////////////////
      //////////////////// Create Donut Chart //////////////////////
      //////////////////////////////////////////////////////////////

      //Create the donut slices and also the invisible arcs for the text
      svg
        .selectAll('.donutArcs')
        .data(pie(labelData))
        .enter()
        .append('path')
        .attr('class', 'donutArcs')
        .attr('d', arc)
        .style('fill', function(d, i) {
          if (d.data.active) {
            return 'url(#mainGradient)'
          }
          return '#000'
        })
        .each(function(d, i) {
          //Search pattern for everything between the start and the first capital L
          const firstArcSection = /(^.+?)L/
          //Grab everything up to the first Line statement
          let newArc = firstArcSection.exec(d3.select(this).attr('d'))[1]
          //Replace all the comma's so that IE can handle it
          newArc = newArc.replace(/,/g, ' ')
          //If the end angle lies beyond a quarter of a circle (90 degrees or pi/2)
          //flip the end and start position
          if (d.endAngle > (90 * Math.PI) / 180) {
            let startLoc = /M(.*?)A/, //Everything between the first capital M and first capital A
              middleLoc = /A(.*?)0 0 1/, //Everything between the first capital A and 0 0 1
              endLoc = /0 0 1 (.*?)$/ //Everything between the first 0 0 1 and the end of the string (denoted by $)
            //Flip the direction of the arc by switching the start en end point (and sweep flag)
            //of those elements that are below the horizontal line
            const newStart = endLoc.exec(newArc)[1]
            const newEnd = startLoc.exec(newArc)[1]
            const middleSec = middleLoc.exec(newArc)[1]

            //Build up the new arc notation, set the sweep-flag to 0
            newArc = 'M' + newStart + 'A' + middleSec + '0 0 0 ' + newEnd
          } //if

          //Create a new invisible arc that the text can flow along
          svg
            .append('path')
            .attr('class', 'hiddenDonutArcs')
            .attr('id', 'donutArc' + i)
            .attr('d', newArc)
            .style('fill', 'none')
        })

      //Append the label names on the outside
      svg
        .selectAll('.donutText')
        .data(pie(labelData))
        .enter()
        .append('text')
        .attr('class', 'donutText')
        //Move the labels below the arcs for those slices with an end angle greater than 90 degrees
        .attr('dy', function(d, i) {
          return d.endAngle > (90 * Math.PI) / 180 ? 18 : -11
        })
        .append('textPath')
        .attr('startOffset', '50%')
        .style('text-anchor', 'middle')
        .style('text-transform', 'uppercase')
        .style('font-size', '13px')
        .attr('font-family', 'CircularStd-Book, serif')
        .attr('xlink:href', function(d, i) {
          return '#donutArc' + i
        })
        .attr('opacity', function(d, i) {
          if (d.data.active) {
            return 1
          }
          return 0.3
        })
        .attr('fill', function(d, i) {
          if (d.data.active) {
            return 'white'
          }
          return '#D8D8D8'
        })
        .text(function(d) {
          return d.data.name
        })

      //////////////////////////////////////////////////////////////
      //////////////////// Generate the data ///////////////////////
      //////////////////////////////////////////////////////////////

      let data = []

      for (let i = 0; i < 1; i++) {
        let point = {}
        let index = 0
        //each feature will be a random number from 1-9
        labelData.forEach(f => {
          if (f.active) {
            //return point[f.name] = 1 + Math.random() * maxScore
            point[f.name] = labelAndScores[f.name]
            index = index + 1
            return point
          }
          return (point[f.name] = 0)
        })
        data.push(point)
      }

      //console.log('aASSASA DATA', data)

      //////////////////////////////////////////////////////////////
      //////////////////// Plotting the axes ///////////////////////
      //////////////////////////////////////////////////////////////

      //labelData = labelData.reverse()

      function angleToCoordinate(angle, value) {
        let x = Math.cos(angle) * radialScale(value)
        let y = Math.sin(angle) * radialScale(value)
        return { x: 0 + x, y: 0 - y }
      }

      // new order for Radar chart so aligns with pie chart slices
      labelData = labelData.sort(
        (a, b) => parseFloat(a.order) - parseFloat(b.order)
      )

      // need to adjust angle so aligns with pie chart slices
      const angleOffset = 0.41

      for (let i = 0; i < labelData.length; i++) {
        let ft_name = labelData[i].name

        let angle =
          Math.PI / 2 + (2 * Math.PI * i) / labelData.length + angleOffset
        let line_coordinate = angleToCoordinate(angle, 10)
        let label_coordinate = angleToCoordinate(angle, 10.5)

        //draw axis line
        // opacity set to 0 as only for DEV purposes
        svg
          .append('line')
          .attr('x1', 0)
          .attr('y1', 0)
          .attr('x2', line_coordinate.x)
          .attr('y2', line_coordinate.y)
          //.attr('transform', 'rotate(22.5 0 0)')
          .attr('stroke', 'yellow')
          .attr('opacity', 0)

        //draw axis label
        //opacity set to 0 as only for DEV purposes
        svg
          .append('text')
          .attr('x', label_coordinate.x)
          .attr('y', label_coordinate.y)
          .attr('fill', 'yellow')
          .attr('opacity', 0)
          .text(ft_name)
      }

      //////////////////////////////////////////////////////////////
      //////////////////// Plotting the data ///////////////////////
      //////////////////////////////////////////////////////////////

      let line = d3
        .line()
        .x(d => d.x)
        .y(d => d.y)

      function getPathCoordinates(data_point) {
        let coordinates = []
        for (let i = 0; i < labelData.length; i++) {
          let ft_name = labelData[i].name
          let angle =
            Math.PI / 2 + (2 * Math.PI * i) / labelData.length + angleOffset
          if (data_point[ft_name] > 0) {
            coordinates.push(angleToCoordinate(angle, data_point[ft_name]))
          }
        }
        return coordinates
      }

      for (let i = 0; i < data.length; i++) {
        let d = data[i]
        let coordinates = getPathCoordinates(d)
        svg
          .append('path')
          .datum(coordinates)
          .attr('d', line)
          .attr('transform', 'scale(2)')
          .style('fill', function(d, i) {
            return 'url(#mainGradient)'
          })
          .attr('stroke-opacity', 1)
          .attr('opacity', 1)
      }
    },
    // https://stackoverflow.com/a/16994725
    animateValue(id, start, end, duration) {
      // assumes integer values for start and end

      const obj = document.getElementById(id)
      const range = end - start
      // no timer shorter than 50ms (not really visible any way)
      const minTimer = 50
      // calc step time to show all interediate values
      let stepTime = Math.abs(Math.floor(duration / range))

      // never go below minTimer
      stepTime = Math.max(stepTime, minTimer)

      // get current time and calculate desired end time
      const startTime = new Date().getTime()
      const endTime = startTime + duration
      let timer

      function run() {
        const now = new Date().getTime()
        const remaining = Math.max((endTime - now) / duration, 0)
        let value = Math.round(end - remaining * range)
        obj.innerHTML = value
        if (value == end) {
          clearInterval(timer)
        }
      }

      timer = setInterval(run, stepTime)
      run()
    }
  }
}
</script>

<style lang="scss">
@import '../scss/variables.scss';
@import '../scss/transitions.scss';
@import '../scss/mixins.scss';

.RadarChart {
  width: 310px;
  height: 310px;
  border-radius: 50%;
  background-image: linear-gradient(
    39deg,
    #000000 0%,
    #1d1c1b 55%,
    #000000 100%
  );
  border: 0 solid rgba(255, 255, 255, 0.1);
  box-shadow: inset 1px 1px 3px 0 rgba(0, 0, 0, 0.5);
  margin: 0 auto 2.5em auto;
  transform-origin: center;
  animation-name: spinIn;
  animation-duration: 0.3s;
  transform: scale(0.9);

  // if iphone 6
  @media only screen and (min-device-width: 414px) and (max-device-width: 736px) and (device-width: 414px) and (device-height: 736px) and (orientation: portrait) and (-webkit-min-device-pixel-ratio: 3) and (-webkit-device-pixel-ratio: 3) {
    transform: scale(0.9);
  }

  @keyframes spinIn {
    from {
      transform: scale(0.6);
    }
    to {
      transform: scale(0.9);
    }
  }

  // Gradients
  .stop-left {
    stop-color: $orchard-01; /* Indigo */
  }
  .stop-center {
    stop-color: $orchard-02; /* Indigo */
  }
  .stop-right {
    stop-color: $orchard-03; /* Teal */
  }

  // Themes
  &--theme--smoke {
    .stop-left {
      stop-color: $smoke-01; /* Indigo */
    }
    .stop-center {
      stop-color: $smoke-02; /* Indigo */
    }
    .stop-right {
      stop-color: $smoke-03; /* Teal */
    }
  }

  &--theme--chard {
    .stop-left {
      stop-color: $chard-01; /* Indigo */
    }
    .stop-center {
      stop-color: $chard-02; /* Indigo */
    }
    .stop-right {
      stop-color: $chard-03; /* Teal */
    }
  }

  // Start accuracy score & level text
  &__meta {
    margin-bottom: 0px;
    font-family: $primary-font;
    text-align: center;
    color: $white;
    font-size: 13px;
    strong {
      font-family: $secondary-font;
      font-size: 28px;
      display: inline-block;
    }
  }

  &__metaItem {
    margin-bottom: 5px;
  }
}
</style>
